import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { ClickableDiv } from '../../common/views/ClickableDiv';
import { SMLListItemBoxRendererProps } from './props';
import { SMLCardSparkline } from './SMLCardSparkline';
import styles from './SMLListItemBox.module.scss';
import { usePreviewPageLang } from '../smlPreview/usePreviewPageLang';

/** The page renders at its own width, then scales into the card's thumbnail. */
const PREVIEW_WIDTH = 420;

const SMLListItemBoxRendererComponent: React.FC<SMLListItemBoxRendererProps> = ({
  onClick,
  isDraft,
  name,
  slug,
  backgroundColor,
  previewURL,
  clicks,
  trend,
}) => {
  const intl = useIntl();
  const fallbackStyle = useMemo(() => ({ backgroundColor }), [backgroundColor]);
  const withLang = usePreviewPageLang();
  const frameUrl = useMemo(() => (previewURL === null ? null : withLang(previewURL)), [previewURL, withLang]);

  return (
    <ClickableDiv onClick={onClick} className={styles.card}>
      <div className={styles.thumb} style={previewURL === null ? fallbackStyle : undefined}>
        {frameUrl !== null && (
          <div className={styles.frame} style={{ width: PREVIEW_WIDTH }}>
            {/* The real page, not a picture of it. pointer-events are off and a
                cover sits on top, so scrolling or tapping inside never happens
                and the whole card stays one click target. Lazy so a full page
                of cards does not load sixteen pages at once. */}
            <iframe
              className={styles.frameDoc}
              src={frameUrl}
              title={name}
              loading="lazy"
              tabIndex={-1}
              scrolling="no"
            />
          </div>
        )}
        <span className={styles.cover} />
      </div>
      <div className={styles.body}>
        <div className={styles.bodyText}>
          <h6 className={styles.name}>{name}</h6>
          <div className={styles.url}>/{slug}</div>
        </div>
        <span className={clsx(styles.pill, !isDraft && styles.pillLive)}>
          {isDraft
            ? intl.formatMessage({ id: 'app.sml.smartlinks.status.draft', defaultMessage: 'Draft' })
            : intl.formatMessage({ id: 'app.sml.smartlinks.status.live', defaultMessage: 'Live' })}
        </span>
      </div>
      {clicks !== undefined && (
        <div className={styles.meta}>
          <span className={styles.clicks}>
            {intl.formatNumber(clicks, { notation: 'compact' })}
            <i className={styles.clicksLabel}>
              {intl.formatMessage({ id: 'app.sml.stats.clicks.short', defaultMessage: 'clicks' })}
            </i>
          </span>
          <SMLCardSparkline values={trend ?? []} />
        </div>
      )}
    </ClickableDiv>
  );
};

export const SMLListItemBoxRenderer = TypedMemo(SMLListItemBoxRendererComponent);
